import React, { useCallback, useEffect, useMemo, useState } from "react";
import mlLogo from "../assets/mercadolibre.png";
import tnLogo from "../assets/tiendanube.png";
import shLogo from "../assets/shopify.png";
import wcLogo from "../assets/woocommerce.png";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

/* ===== ML OAuth (WEB) ===== */
const MLI_APP_ID = process.env.REACT_APP_MLI_APP_ID || "";
const MLI_REDIRECT_URI = process.env.REACT_APP_MLI_REDIRECT_URI || "";
const MLI_AUTH_BASE = "https://auth.mercadolibre.com.ar/authorization";

function toBase64Json(obj: unknown) {
  const json = JSON.stringify(obj);
  return window.btoa(unescape(encodeURIComponent(json)));
}

function buildMeliAuthUrl(stateObj?: Record<string, unknown>) {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: String(MLI_APP_ID),
    redirect_uri: String(MLI_REDIRECT_URI),
  });

  if (stateObj) {
    const stateB64 = toBase64Json(stateObj);
    params.set("state", stateB64);
  }

  return `${MLI_AUTH_BASE}?${params.toString()}`;
}

const PROVIDERS = [
  { value: "mercadolibre", label: "Mercado Libre" },
  { value: "tiendanube", label: "Tienda Nube" },
  { value: "shopify", label: "Shopify" },
  { value: "woocommerce", label: "WooCommerce" },
  { value: "otros", label: "Otros" },
] as const;

/* ===== Types ===== */
type ProviderValue = (typeof PROVIDERS)[number]["value"];

type StoreApiRaw = Record<string, unknown>;

type UserApiRow = {
  id?: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  rol?: string;
};

type StoreItem = {
  id: number | string | null;
  slug: string;
  name: string;
  business_name: string;
};

type SellerOption = {
  id: number;
  userID: number | null;
  label: string;
  fullName: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phone: string;
  razonSocial: string;
};

type LinkedSeller = {
  id: number;
  userID: number | null;
  razon_social?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
  phone?: string;
};

type AccountItem = {
  id: number;
  provider?: string;
  display_name?: string;
  displayName?: string;
  account_identifier?: string;
  accountIdentifier?: string;
  ml_user_id?: number | null;
  mlUserId?: number | null;
  auto_entry?: boolean | null;
  autoEntry?: boolean | null;
  __hasOAuthToken?: boolean;
  __tokenMlUserId?: number | null;
  __resolvedProvider?: string;
};

type TokenRecord = {
  id?: number;
  account_id?: number;
  user_id?: number | string | null;
  access_token?: string | null;
  refresh_token?: string | null;
  expires_at?: number | string | null;
};

type StoresResponse = {
  ok?: boolean;
  error?: string;
  stores?: StoreApiRaw[];
  data?: StoreApiRaw[];
  result?: StoreApiRaw[];
};

type SellersResponse = {
  ok?: boolean;
  error?: string;
  sellers?: Array<Record<string, unknown>>;
  data?: Array<Record<string, unknown>>;
  result?: Array<Record<string, unknown>>;
};

type UsersResponse = {
  ok?: boolean;
  error?: string;
  usuarios?: UserApiRow[];
  data?: UserApiRow[];
  result?: UserApiRow[];
};

type AccountsResponse = {
  ok?: boolean;
  error?: string;
  accounts?: AccountItem[];
  data?: AccountItem[];
  result?: AccountItem[];
};

type LinkedSellersResponse = {
  ok?: boolean;
  error?: string;
  sellers?: LinkedSeller[];
  seller?: LinkedSeller;
  data?: LinkedSeller[] | LinkedSeller;
  result?: LinkedSeller[] | LinkedSeller;
};

type TokensResponse = {
  ok?: boolean;
  error?: string;
  tokens?: TokenRecord | TokenRecord[] | null;
  data?: TokenRecord | TokenRecord[] | null;
  result?: TokenRecord | TokenRecord[] | null;
};

type CreateAccountResponse = {
  ok?: boolean;
  error?: string;
  accountID?: number;
};

type GenericResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
};

type ModalProps = {
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  width?: string;
};

type IconBtnProps = {
  title: string;
  onClick: (e?: React.MouseEvent) => void;
  children: React.ReactNode;
  className?: string;
};

type TabBtnProps = {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
};

type CreateStoreModalProps = {
  token: string | null;
  onClose: () => void;
  onCreated?: () => Promise<void> | void;
};

type ViewStoreModalProps = {
  store: StoreItem;
  onClose: () => void;
};

type EditStoreModalProps = {
  token: string | null;
  store: StoreItem;
  allSellers: SellerOption[];
  onClose: () => void;
  onChanged?: () => Promise<void> | void;
};

type AccountDetailModalProps = {
  storeId: number | string;
  account: AccountItem;
  onClose: () => void;
  token: string | null;
  onUpdated?: () => Promise<void> | void;
};

/* ===== Logos ===== */
const LOGO_BY_PROVIDER: Record<string, string | undefined> = {
  mercadolibre: mlLogo,
  tiendanube: tnLogo,
  shopify: shLogo,
  woocommerce: wcLogo,
};

const pageWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const subtitleStyle: React.CSSProperties = {
  margin: "8px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "15px",
  lineHeight: 1.6,
};

const actionsWrapStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const statsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "16px",
};

const kpiLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginBottom: "10px",
};

const kpiValueStyle: React.CSSProperties = {
  fontSize: "28px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const kpiMetaStyle: React.CSSProperties = {
  marginTop: "10px",
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const sectionTextStyle: React.CSSProperties = {
  margin: "6px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.02)",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 12px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  background: "rgba(255,255,255,0.02)",
};

const tdStyle: React.CSSProperties = {
  padding: "14px 12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};

const iconBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--border)",
  borderRadius: "12px",
  minWidth: "38px",
  minHeight: "38px",
  cursor: "pointer",
  color: "var(--text-primary)",
  fontSize: "16px",
};

const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const modalBackdropStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
};

const modalHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 18px",
  borderBottom: "1px solid var(--border)",
};

const modalBodyStyle: React.CSSProperties = {
  padding: "18px",
  overflowY: "auto",
  maxHeight: "72vh",
};

const smallMutedStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
};

const providerCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.03)",
  padding: "14px",
};

const contactCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.03)",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "48px",
  padding: "0 14px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
};

const switchStyle: React.CSSProperties = {
  position: "relative",
  width: "52px",
  height: "30px",
  borderRadius: "999px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  cursor: "pointer",
};

function AccountLogo({
  provider,
  className = "h-5 w-auto",
}: {
  provider?: string;
  className?: string;
}) {
  const src = LOGO_BY_PROVIDER[String(provider || "").toLowerCase()];
  if (!src) {
    return (
      <span style={{ fontSize: "12px", textTransform: "capitalize" }}>
        {provider || "—"}
      </span>
    );
  }
  return <img src={src} alt={provider} className={className} />;
}

/* ===== UI ===== */
const IconBtn = ({ title, onClick, children, className = "" }: IconBtnProps) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={className}
    style={iconBtnStyle}
  >
    {children}
  </button>
);

const Modal = ({ title, onClose, children, width = "1100px" }: ModalProps) => (
  <div style={modalOverlayStyle}>
    <div style={modalBackdropStyle} onClick={onClose} />
    <div
      style={{
        position: "relative",
        width: "95vw",
        maxWidth: width,
        maxHeight: "85vh",
        overflow: "hidden",
      }}
    >
      <Card style={{ padding: 0 }}>
        <div style={modalHeaderStyle}>
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            {title}
          </h3>

          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              fontSize: "22px",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={modalBodyStyle}>{children}</div>
      </Card>
    </div>
  </div>
);

const TabBtn = ({ active, onClick, children }: TabBtnProps) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      minHeight: "42px",
      padding: "0 16px",
      borderRadius: "999px",
      border: active ? "1px solid var(--border)" : "1px solid transparent",
      background: active ? "var(--primary-soft)" : "transparent",
      color: active ? "var(--text-primary)" : "var(--text-secondary)",
      fontSize: "14px",
      fontWeight: active ? 700 : 600,
      cursor: "pointer",
    }}
  >
    {children}
  </button>
);

/* ===== Helpers ===== */
const pickStoresArray = (payload: StoresResponse) =>
  payload?.stores || payload?.data || payload?.result || [];

const pickSellersArray = (payload: SellersResponse) =>
  payload?.sellers || payload?.data || payload?.result || [];

const pickUsersArray = (payload: UsersResponse) =>
  payload?.usuarios || payload?.data || payload?.result || [];

const pickAccountsArray = (payload: AccountsResponse) =>
  payload?.accounts || payload?.data || payload?.result || [];

const pickLinkedSellersArray = (payload: LinkedSellersResponse): LinkedSeller[] => {
  if (Array.isArray(payload?.sellers)) return payload.sellers;
  if (payload?.seller && typeof payload.seller === "object") return [payload.seller];
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data && !Array.isArray(payload.data)) return [payload.data];
  if (Array.isArray(payload?.result)) return payload.result;
  if (payload?.result && !Array.isArray(payload.result)) return [payload.result];
  return [];
};

const pickTokenRecord = (payload: TokensResponse): TokenRecord | null => {
  if (payload?.tokens && !Array.isArray(payload.tokens)) return payload.tokens;
  if (Array.isArray(payload?.tokens)) return payload.tokens[0] || null;
  if (payload?.data && !Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload?.data)) return payload.data[0] || null;
  if (payload?.result && !Array.isArray(payload.result)) return payload.result;
  if (Array.isArray(payload?.result)) return payload.result[0] || null;
  return null;
};

const hasValidMlToken = (tokenRecord: TokenRecord | null) => {
  if (!tokenRecord) return false;
  return Boolean(tokenRecord.access_token && tokenRecord.refresh_token);
};

const normalizeStore = (x: StoreApiRaw): StoreItem => {
  const id = (x?.id as number | string | null | undefined) ?? null;

  const name =
    (x?.nombre_fantasia as string | undefined) ??
    (x?.nombreFantasia as string | undefined) ??
    (x?.name as string | undefined) ??
    (x?.store_name as string | undefined);

  const categoria =
    (x?.categoria as string | undefined) ??
    (x?.business_name as string | undefined) ??
    (x?.businessName as string | undefined);

  return {
    id,
    slug: id != null ? String(id) : "—",
    name: name ?? "—",
    business_name: categoria ?? "—",
  };
};

function buildFullName(firstName?: string, lastName?: string) {
  return [firstName || "", lastName || ""]
    .map((v) => String(v).trim())
    .filter(Boolean)
    .join(" ");
}

function normalizeSellerOptions(
  sellerRows: Array<Record<string, unknown>>,
  userRows: UserApiRow[]
): SellerOption[] {
  const userMap = new Map<number, UserApiRow>();

  (userRows || []).forEach((user) => {
    if (user?.id != null) {
      userMap.set(Number(user.id), user);
    }
  });

  return (sellerRows || [])
    .filter((x) => x?.id != null)
    .map((x) => {
      const sellerId = Number(x.id);
      const userID =
        x?.userID != null
          ? Number(x.userID)
          : x?.user_id != null
          ? Number(x.user_id)
          : null;

      const user = userID != null ? userMap.get(userID) : undefined;

      const firstName =
        (user?.first_name as string | undefined) ||
        (x?.first_name as string | undefined) ||
        (x?.firstName as string | undefined) ||
        "";

      const lastName =
        (user?.last_name as string | undefined) ||
        (x?.last_name as string | undefined) ||
        (x?.lastName as string | undefined) ||
        "";

      const fullName =
        buildFullName(firstName, lastName) ||
        (x?.full_name as string | undefined) ||
        (x?.fullName as string | undefined) ||
        "";

      const username =
        (user?.username as string | undefined) ||
        (x?.username as string | undefined) ||
        "";

      const email =
        (user?.email as string | undefined) ||
        (x?.email as string | undefined) ||
        "";

      const phone =
        (user?.phone as string | undefined) ||
        (x?.phone as string | undefined) ||
        "";

      const razonSocial =
        (x?.razon_social as string | undefined) ||
        (x?.razonSocial as string | undefined) ||
        "";

      const label = [fullName || username || `Seller ${sellerId}`, razonSocial]
        .filter(Boolean)
        .join(" — ");

      return {
        id: sellerId,
        userID,
        label,
        fullName,
        firstName,
        lastName,
        username,
        email,
        phone,
        razonSocial,
      };
    });
}

function enrichLinkedSellers(
  rows: LinkedSeller[],
  allSellers: SellerOption[]
): LinkedSeller[] {
  const sellerMap = new Map<number, SellerOption>();
  allSellers.forEach((seller) => {
    sellerMap.set(seller.id, seller);
  });

  return (rows || []).map((row) => {
    const extra = sellerMap.get(Number(row.id));

    return {
      ...row,
      userID: row.userID ?? extra?.userID ?? null,
      razon_social: row.razon_social || extra?.razonSocial || "",
      fullName: row.fullName || extra?.fullName || "",
      firstName: row.firstName || extra?.firstName || "",
      lastName: row.lastName || extra?.lastName || "",
      username: row.username || extra?.username || "",
      email: row.email || extra?.email || "",
      phone: row.phone || extra?.phone || "",
    };
  });
}

const normProvider = (p?: string) => String(p || "").toLowerCase();
const isMeli = (p?: string) => normProvider(p) === "mercadolibre";
const isTokenMeliCandidate = (tokenRecord: TokenRecord | null) =>
  Boolean(tokenRecord?.access_token && tokenRecord?.refresh_token && tokenRecord?.user_id != null);

const getAccDisplayName = (a: AccountItem) =>
  a?.display_name ||
  a?.displayName ||
  a?.account_identifier ||
  a?.accountIdentifier ||
  "—";

const getAccIdentifier = (a: AccountItem) =>
  a?.account_identifier || a?.accountIdentifier || "—";

const getAccMlUserId = (a: AccountItem) => {
  const rawValue = a?.ml_user_id ?? a?.mlUserId ?? a?.__tokenMlUserId ?? null;

  if (rawValue == null) return null;

  const parsed = Number(rawValue);
  return Number.isNaN(parsed) ? null : parsed;
};

const getResolvedProvider = (a: AccountItem) => {
  if (a?.__resolvedProvider) return a.__resolvedProvider;
  if (a?.provider) return a.provider;
  if (a?.__hasOAuthToken && a?.__tokenMlUserId != null) return "Mercadolibre";
  return "";
};

const getAutoEntry = (a: AccountItem) => Boolean(a?.auto_entry ?? a?.autoEntry ?? false);

const isMeliLinked = (a: AccountItem) =>
  isMeli(getResolvedProvider(a)) &&
  getAccMlUserId(a) != null &&
  a.__hasOAuthToken === true;

async function readJsonSafe(resp: Response | null) {
  if (!resp) return {};
  if (resp.status === 204) return { ok: true };
  const ct = resp.headers?.get?.("content-type") || "";
  if (ct.includes("application/json")) {
    return resp.json().catch(() => ({}));
  }
  const txt = await resp.text().catch(() => "");
  return txt ? { ok: false, error: txt } : {};
}

function bestError(
  resp: Response | null,
  body: Record<string, unknown>,
  fallback = "Error"
) {
  if (typeof body?.error === "string") return body.error;
  if (typeof body?.message === "string") return body.message;
  if (resp?.status) return `${fallback} (HTTP ${resp.status})`;
  return fallback;
}

async function updateAccountEntryOnly(
  token: string | null,
  accountId: number,
  autoEntry: boolean
) {
  const attempts = [
    {
      method: "PUT",
      body: { auto_entry: autoEntry },
    },
    {
      method: "PUT",
      body: { entry: autoEntry },
    },
    {
      method: "PUT",
      body: { value: autoEntry },
    },
    {
      method: "PATCH",
      body: { auto_entry: autoEntry },
    },
  ] as const;

  let lastError = "No se pudo actualizar auto-entry";

  for (const attempt of attempts) {
    try {
      const resp = await authFetch(`/v2/accounts/${accountId}/entry`, {
        method: attempt.method,
        token,
        json: true,
        body: JSON.stringify(attempt.body),
      });

      const json = (await readJsonSafe(resp)) as GenericResponse;
      if (resp.ok && json?.ok !== false) return;

      lastError = bestError(
        resp,
        json as Record<string, unknown>,
        "No se pudo actualizar auto-entry"
      );
    } catch (error) {
      lastError = error instanceof Error ? error.message : lastError;
    }
  }

  throw new Error(lastError);
}

async function updateAccountSafely(
  token: string | null,
  account: {
    id: number;
    provider: string;
    display_name: string;
    ml_user_id: number | null;
    auto_entry: boolean;
  }
) {
  const payload = {
    account: {
      provider: account.provider,
      display_name: account.display_name,
      MLUserID: account.ml_user_id,
      auto_entry: account.auto_entry,
    },
  };

  const resp = await authFetch(`/v2/accounts/${account.id}`, {
    method: "PUT",
    token,
    json: true,
    body: JSON.stringify(payload),
  });

  const json = (await readJsonSafe(resp)) as GenericResponse;

  if (!resp.ok || json?.ok === false) {
    throw new Error(
      bestError(resp, json as Record<string, unknown>, "No se pudo actualizar la cuenta")
    );
  }
}

/* ===== Página ===== */
export default function Stores() {
  const { token } = useAuth();

  const [stores, setStores] = useState<StoreItem[]>([]);
  const [sellers, setSellers] = useState<SellerOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [editStore, setEditStore] = useState<StoreItem | null>(null);
  const [viewStore, setViewStore] = useState<StoreItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [stRes, seRes, usRes] = await Promise.all([
        authFetch("/v2/stores", { token }),
        authFetch("/v2/sellers", { token }),
        authFetch("/v2/users", { token }),
      ]);

      const st: StoresResponse = await stRes.json().catch(() => ({}));
      const se: SellersResponse = await seRes.json().catch(() => ({}));
      const us: UsersResponse = await usRes.json().catch(() => ({}));

      if (!stRes.ok || st?.ok === false) {
        throw new Error(st?.error || "No se pudo cargar Tiendas");
      }

      setStores((pickStoresArray(st) || []).map(normalizeStore));

      if (!seRes.ok || se?.ok === false) {
        setSellers([]);
      } else {
        const sellerRows = pickSellersArray(se) || [];
        const userRows = usRes.ok && us?.ok !== false ? pickUsersArray(us) || [] : [];
        setSellers(normalizeSellerOptions(sellerRows, userRows));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const deleteStore = async (storeId: number | string | null) => {
    const ok = window.confirm(`¿Eliminar tienda ID ${storeId}?`);
    if (!ok || storeId == null) return;

    try {
      const r = await authFetch(`/v2/stores/${storeId}`, {
        method: "DELETE",
        token,
      });
      const j = (await readJsonSafe(r)) as GenericResponse;

      if (!r.ok || j?.ok === false) {
        throw new Error(
          bestError(r, j as Record<string, unknown>, "No se pudo eliminar la tienda")
        );
      }

      await load();
    } catch (e) {
      console.error("deleteStore error:", e);
      alert(e instanceof Error ? e.message : "Error eliminando tienda");
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const totalStores = stores.length;
  const totalConCategoria = useMemo(
    () => stores.filter((s) => s.business_name && s.business_name !== "—").length,
    [stores]
  );
  const totalSellersDisponibles = sellers.length;

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Tiendas</h1>
          <p style={subtitleStyle}>
            Gestión de tiendas, sellers vinculados y cuentas de e-commerce conectadas.
          </p>
        </div>

        <div style={actionsWrapStyle}>
          <Button variant="secondary" onClick={load}>
            Actualizar
          </Button>
          <Button onClick={() => setCreateOpen(true)}>Nueva tienda</Button>
        </div>
      </div>

      <div style={statsGridStyle} className="stores-kpi-grid">
        <Card>
          <div style={kpiLabelStyle}>Tiendas cargadas</div>
          <div style={kpiValueStyle}>{totalStores}</div>
          <div style={kpiMetaStyle}>Cantidad total de tiendas registradas.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Con categoría</div>
          <div style={kpiValueStyle}>{totalConCategoria}</div>
          <div style={kpiMetaStyle}>Tiendas que ya tienen categoría definida.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Sellers disponibles</div>
          <div style={kpiValueStyle}>{totalSellersDisponibles}</div>
          <div style={kpiMetaStyle}>Opciones disponibles para asignación.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Estado</div>
          <div style={kpiValueStyle}>{loading ? "..." : "OK"}</div>
          <div style={kpiMetaStyle}>{error ? error : "Módulo operativo."}</div>
        </Card>
      </div>

      <Card>
        <div style={{ marginBottom: "14px" }}>
          <h2 style={sectionTitleStyle}>Listado de tiendas</h2>
          <p style={sectionTextStyle}>
            Vista general con acceso a ver, editar y eliminar cada tienda.
          </p>
        </div>

        {error && (
          <div
            style={{ marginBottom: "16px", color: "var(--danger)", fontSize: "14px" }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
            Cargando…
          </div>
        ) : stores.length === 0 ? (
          <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
            Sin tiendas aún.
          </div>
        ) : (
          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Código</th>
                  <th style={thStyle}>Nombre fantasía</th>
                  <th style={thStyle}>Categoría</th>
                  <th style={thStyle}>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {stores.map((s) => (
                  <tr key={s.id ?? `${s.slug}-${s.name}`}>
                    <td style={tdStyle}>
                      <span style={{ fontFamily: "monospace" }}>{s.slug}</span>
                    </td>
                    <td style={tdStyle}>{s.name}</td>
                    <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>
                      {s.business_name || "—"}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <IconBtn title="Ver" onClick={() => setViewStore(s)}>
                          👁
                        </IconBtn>
                        <IconBtn title="Editar" onClick={() => setEditStore(s)}>
                          ✏️
                        </IconBtn>
                        <IconBtn title="Eliminar" onClick={() => deleteStore(s.id)}>
                          🗑
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {createOpen && (
        <CreateStoreModal
          token={token}
          onClose={() => setCreateOpen(false)}
          onCreated={async () => {
            setCreateOpen(false);
            await load();
          }}
        />
      )}

      {viewStore && (
        <ViewStoreModal store={viewStore} onClose={() => setViewStore(null)} />
      )}

      {editStore && (
        <EditStoreModal
          token={token}
          store={editStore}
          allSellers={sellers}
          onClose={() => setEditStore(null)}
          onChanged={async () => {
            await load();
          }}
        />
      )}

      <style>
        {`
          .stores-kpi-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          @media (max-width: 1180px) {
            .stores-kpi-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 720px) {
            .stores-kpi-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}

/* ================== Crear Store ================== */
function CreateStoreModal({ token, onClose, onCreated }: CreateStoreModalProps) {
  const [form, setForm] = useState({ name: "", business_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const create = async () => {
    setSubmitting(true);
    setErr(null);

    try {
      const payload = {
        nombre_fantasia: form.name?.trim(),
        categoria: form.business_name?.trim() || null,
      };

      const r = await authFetch("/v2/stores", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const j: GenericResponse = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j?.error || "No se pudo crear la tienda");

      await onCreated?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error creando tienda");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Nueva tienda" onClose={onClose}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: "16px",
        }}
        className="create-store-grid"
      >
        <div>
          <label style={{ ...smallMutedStyle, display: "block", marginBottom: "8px" }}>
            Nombre (fantasía) *
          </label>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="PENSADO POR BEBES"
          />
        </div>

        <div>
          <label style={{ ...smallMutedStyle, display: "block", marginBottom: "8px" }}>
            Categoría
          </label>
          <Input
            value={form.business_name}
            onChange={(e) =>
              setForm((f) => ({ ...f, business_name: e.target.value }))
            }
            placeholder="MODA"
          />
        </div>
      </div>

      <div style={{ marginTop: "20px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
        <Button onClick={create} disabled={submitting || !form.name.trim()}>
          {submitting ? "Creando..." : "Crear tienda"}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
      </div>

      {err && (
        <div style={{ marginTop: "12px", color: "var(--danger)", fontSize: "14px" }}>
          {err}
        </div>
      )}

      <style>
        {`
          @media (max-width: 720px) {
            .create-store-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </Modal>
  );
}

/* ================== Ver Store ================== */
function ViewStoreModal({ store, onClose }: ViewStoreModalProps) {
  return (
    <Modal title={`Ver tienda: ${store.name}`} onClose={onClose} width="900px">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "16px",
        }}
        className="view-store-grid"
      >
        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Código (ID)</div>
          <div style={{ marginTop: "6px", fontFamily: "monospace" }}>{store.id}</div>
        </div>

        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Nombre</div>
          <div style={{ marginTop: "6px" }}>{store.name}</div>
        </div>

        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Categoría</div>
          <div style={{ marginTop: "6px", color: "var(--text-secondary)" }}>
            {store.business_name || "—"}
          </div>
        </div>
      </div>

      <div style={{ marginTop: "20px", textAlign: "right" }}>
        <Button variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      <style>
        {`
          @media (max-width: 720px) {
            .view-store-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </Modal>
  );
}

/* ================== Editar Store ================== */
function EditStoreModal({
  token,
  store,
  allSellers,
  onClose,
  onChanged,
}: EditStoreModalProps) {
  const [tab, setTab] = useState("CUENTAS");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [sellerSearch, setSellerSearch] = useState("");
  const [addingSellerId, setAddingSellerId] = useState("");
  const [linkedSellers, setLinkedSellers] = useState<LinkedSeller[]>([]);

  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<AccountItem | null>(null);

  const [addingAcc, setAddingAcc] = useState<{
    provider: ProviderValue;
    accountIdentifier: string;
    displayName: string;
  }>({
    provider: "mercadolibre",
    accountIdentifier: "",
    displayName: "",
  });

  const loadLinkedSellers = async () => {
    try {
      const tryEndpoints = [
        `/v2/stores/${store.id}/seller`,
        `/v2/sellers/by-store/${store.id}`,
      ];

      for (const endpoint of tryEndpoints) {
        const r = await authFetch(endpoint, { token });
        const j: LinkedSellersResponse = await r.json().catch(() => ({}));
        if (r.ok && j?.ok !== false) {
          const arr = pickLinkedSellersArray(j);
          if (arr.length) {
            setLinkedSellers(enrichLinkedSellers(arr, allSellers));
            return;
          }
        }
      }

      setLinkedSellers([]);
    } catch {
      setLinkedSellers([]);
    }
  };

  const decorateAccountsWithOAuthStatus = async (
    rawAccounts: AccountItem[]
  ): Promise<AccountItem[]> => {
    return Promise.all(
      (rawAccounts || []).map(async (account) => {
        try {
          const r = await authFetch(`/v2/tokens/${account.id}`, { token });
          const j: TokensResponse = await r.json().catch(() => ({}));

          if (!r.ok || j?.ok === false) {
            return {
              ...account,
              __hasOAuthToken: false,
              __tokenMlUserId: null,
              __resolvedProvider: account.provider || "",
            };
          }

          const tokenRecord = pickTokenRecord(j);
          const tokenMlUserId =
            tokenRecord?.user_id != null && tokenRecord.user_id !== ""
              ? Number(tokenRecord.user_id)
              : null;

          const hasMlToken = hasValidMlToken(tokenRecord);
          const resolvedProvider =
            account.provider ||
            (isTokenMeliCandidate(tokenRecord) ? "Mercadolibre" : "");

          return {
            ...account,
            __hasOAuthToken: hasMlToken,
            __tokenMlUserId: tokenMlUserId,
            __resolvedProvider: resolvedProvider,
          };
        } catch {
          return {
            ...account,
            __hasOAuthToken: false,
            __tokenMlUserId: null,
            __resolvedProvider: account.provider || "",
          };
        }
      })
    );
  };

  const loadAccounts = async () => {
    try {
      const r = await authFetch(`/v2/stores/${store.id}/account`, { token });
      const j: AccountsResponse = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(j?.error || "No se pudieron cargar cuentas");
      }

      const baseAccounts = pickAccountsArray(j) || [];
      const withStatus = await decorateAccountsWithOAuthStatus(baseAccounts);
      setAccounts(withStatus);
    } catch {
      setAccounts([]);
    }
  };

  useEffect(() => {
    loadAccounts();
    loadLinkedSellers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const filteredSellers = useMemo(() => {
    const q = sellerSearch.trim().toLowerCase();
    if (!q) return allSellers || [];
    return (allSellers || []).filter((s) =>
      [
        s.label,
        s.fullName,
        s.username,
        s.email,
        s.phone,
        s.razonSocial,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [allSellers, sellerSearch]);

  const addSeller = async () => {
    if (!addingSellerId) return;
    setBusy(true);
    setErr(null);

    try {
      const r = await authFetch(`/v2/stores/${store.id}/seller`, {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({
          sellerID: Number(addingSellerId),
        }),
      });

      const j: GenericResponse = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(j?.error || "No se pudo vincular seller");
      }

      setAddingSellerId("");
      setSellerSearch("");
      await onChanged?.();
      await loadLinkedSellers();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error vinculando seller");
    } finally {
      setBusy(false);
    }
  };

  const createAndLinkAccount = async () => {
    setBusy(true);
    setErr(null);

    try {
      const provider = normProvider(
        addingAcc.provider || "mercadolibre"
      ) as ProviderValue;

      const displayName = (addingAcc.displayName || "").trim() || "Pendiente";

      const createPayload = {
        provider,
        display_name: displayName,
        MLUserID: provider === "mercadolibre" ? null : null,
        auto_entry: false,
      };

      const r1 = await authFetch(`/v2/accounts`, {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify(createPayload),
      });

      const j1: CreateAccountResponse = await r1.json().catch(() => ({}));
      if (!r1.ok || !j1.ok || !j1.accountID) {
        throw new Error(j1?.error || "No se pudo crear la account");
      }

      const accountID = Number(j1.accountID);

      const r2 = await authFetch(`/v2/stores/${store.id}/account`, {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({ accountID, storeID: Number(store.id) }),
      });

      const j2: GenericResponse = await r2.json().catch(() => ({}));
      if (!r2.ok || !j2.ok) {
        throw new Error(j2?.error || "No se pudo vincular la account al store");
      }

      await loadAccounts();
      await onChanged?.();
      setAddingAcc({
        provider: "mercadolibre",
        accountIdentifier: "",
        displayName: "",
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error agregando cuenta");
    } finally {
      setBusy(false);
    }
  };

  const deleteAccount = async (account: AccountItem) => {
    const ok = window.confirm(
      `¿Eliminar la cuenta ID ${account?.id}?${
        isMeli(getResolvedProvider(account)) ? " (MercadoLibre)" : ""
      }\n\nEsto puede fallar si el backend no permite borrar por FK.`
    );
    if (!ok) return;

    setBusy(true);
    setErr(null);

    const accountID = Number(account?.id);
    const storeID = Number(store?.id);

    try {
      try {
        const rUnlink = await authFetch(`/v2/stores/${storeID}/account`, {
          method: "DELETE",
          token,
          json: true,
          body: JSON.stringify({ accountID, storeID }),
        });

        if (!rUnlink.ok && ![404, 405].includes(rUnlink.status)) {
          const jUnlink = await readJsonSafe(rUnlink);
          console.warn("unlink account warning:", jUnlink);
        }
      } catch (e) {
        console.warn("unlink account fetch failed (ignored):", e);
      }

      const rDel = await authFetch(`/v2/accounts/${accountID}`, {
        method: "DELETE",
        token,
      });
      const jDel = (await readJsonSafe(rDel)) as GenericResponse;

      if (!rDel.ok || jDel?.ok === false) {
        throw new Error(
          bestError(rDel, jDel as Record<string, unknown>, "No se pudo eliminar la account")
        );
      }

      if (selectedAccount?.id === accountID) setSelectedAccount(null);
      await loadAccounts();
      await onChanged?.();
    } catch (e) {
      console.error("deleteAccount error:", e);
      setErr(e instanceof Error ? e.message : "Error eliminando account");
    } finally {
      setBusy(false);
    }
  };

  const toggleEntry = async (account: AccountItem) => {
    const nextValue = !getAutoEntry(account);
    setBusy(true);
    setErr(null);

    try {
      await updateAccountEntryOnly(token, Number(account.id), nextValue);
      await loadAccounts();
      await onChanged?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error actualizando auto-entry");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Editar tienda: ${store.name}`} onClose={onClose} width="1280px">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "16px",
          marginBottom: "20px",
        }}
        className="edit-store-head-grid"
      >
        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Código (ID)</div>
          <div style={{ marginTop: "6px", fontFamily: "monospace" }}>{store.id}</div>
        </div>

        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Nombre</div>
          <div style={{ marginTop: "6px" }}>{store.name}</div>
        </div>

        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Categoría</div>
          <div style={{ marginTop: "6px", color: "var(--text-secondary)" }}>
            {store.business_name || "—"}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "24px" }}>
        <TabBtn active={tab === "GENERAL"} onClick={() => setTab("GENERAL")}>
          General
        </TabBtn>
        <TabBtn active={tab === "CUENTAS"} onClick={() => setTab("CUENTAS")}>
          Cuentas
        </TabBtn>
        <TabBtn active={tab === "DIRECCIONES"} onClick={() => setTab("DIRECCIONES")}>
          Direcciones
        </TabBtn>
        <TabBtn active={tab === "CONTACTOS"} onClick={() => setTab("CONTACTOS")}>
          Contactos
        </TabBtn>
      </div>

      {tab === "GENERAL" && (
        <Card>
          <div style={{ color: "var(--text-secondary)", fontSize: "14px", lineHeight: 1.6 }}>
            MVP: edición de datos generales de la tienda pendiente de backend estable para
            update.
          </div>
        </Card>
      )}

      {tab === "CUENTAS" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
          }}
          className="edit-store-tabs-grid"
        >
          <Card>
            <h4 style={{ ...sectionTitleStyle, marginBottom: "14px" }}>Asignar seller</h4>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                marginBottom: "14px",
              }}
            >
              {linkedSellers.length === 0 ? (
                <div style={{ fontSize: "14px", color: "var(--text-secondary)" }}>
                  Sin sellers vinculados.
                </div>
              ) : (
                linkedSellers.map((s) => {
                  const displayName =
                    s.fullName ||
                    s.username ||
                    s.razon_social ||
                    `Seller ${s.id}`;

                  return (
                    <div
                      key={s.id}
                      style={{
                        border: "1px solid var(--border)",
                        borderRadius: "16px",
                        background: "rgba(255,255,255,0.03)",
                        padding: "12px 14px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "var(--text-primary)",
                        }}
                      >
                        {displayName}
                      </div>

                      {s.razon_social ? (
                        <div style={{ ...smallMutedStyle, marginTop: "4px" }}>
                          {s.razon_social}
                        </div>
                      ) : null}

                      <div style={{ ...smallMutedStyle, marginTop: "4px" }}>
                        Seller ID: {s.id}
                        {s.username ? ` · @${s.username}` : ""}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div style={{ marginBottom: "12px" }}>
              <Input
                value={sellerSearch}
                onChange={(e) => setSellerSearch(e.target.value)}
                placeholder="Buscar seller..."
              />
            </div>

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <select
                value={addingSellerId}
                onChange={(e) => setAddingSellerId(e.target.value)}
                style={{
                  flex: 1,
                  minHeight: "48px",
                  padding: "0 14px",
                  borderRadius: "16px",
                  border: "1px solid var(--border)",
                  background: "var(--surface-alt)",
                  color: "var(--text-primary)",
                }}
              >
                <option value="">Agregar seller...</option>
                {filteredSellers.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {[
                      s.fullName || s.username || `Seller ${s.id}`,
                      s.razonSocial || "",
                    ]
                      .filter(Boolean)
                      .join(" — ")}{" "}
                    (ID {s.id})
                  </option>
                ))}
              </select>

              <Button onClick={addSeller} disabled={busy || !addingSellerId}>
                Agregar
              </Button>
            </div>

            <div style={{ ...smallMutedStyle, marginTop: "12px" }}>
              El selector prioriza nombre y apellido del usuario seller, y mantiene razón
              social como apoyo visual.
            </div>
          </Card>

          <Card>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                marginBottom: "14px",
                flexWrap: "wrap",
              }}
            >
              <h4 style={{ ...sectionTitleStyle, margin: 0 }}>Cuentas de e-commerce</h4>
              <Button variant="secondary" onClick={loadAccounts} disabled={busy}>
                Actualizar cuentas
              </Button>
            </div>

            {accounts.length === 0 ? (
              <div
                style={{
                  fontSize: "14px",
                  color: "var(--text-secondary)",
                  marginBottom: "14px",
                }}
              >
                Sin cuentas aún.
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginBottom: "16px",
                }}
              >
                {accounts.map((a) => {
                  const resolvedProvider = getResolvedProvider(a);
                  const linked = isMeliLinked(a);
                  const mlUserId = getAccMlUserId(a);
                  const autoEntry = getAutoEntry(a);

                  return (
                    <div
                      key={a.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        border: "1px solid var(--border)",
                        borderRadius: "16px",
                        background: "rgba(255,255,255,0.03)",
                        padding: "12px 14px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedAccount(a)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          background: "transparent",
                          border: "none",
                          color: "inherit",
                          textAlign: "left",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <AccountLogo provider={resolvedProvider} />

                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "var(--text-primary)",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              flexWrap: "wrap",
                            }}
                          >
                            {getAccDisplayName(a)}
                            {linked ? (
                              <span style={{ color: "var(--success)" }}>✓ Vinculado</span>
                            ) : isMeli(resolvedProvider) ? (
                              <span style={{ color: "var(--text-secondary)" }}>• pendiente</span>
                            ) : null}
                          </div>

                          <div
                            style={{
                              ...smallMutedStyle,
                              marginTop: "4px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {isMeli(resolvedProvider) ? (
                              mlUserId != null ? (
                                <>ML user_id: {mlUserId}</>
                              ) : (
                                <>Mercado Libre sin metadata completa</>
                              )
                            ) : (
                              <>{getAccIdentifier(a)}</>
                            )}
                          </div>
                        </div>

                        <div style={{ ...smallMutedStyle, fontFamily: "monospace" }}>
                          ID {a.id}
                        </div>
                      </button>

                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          minWidth: "120px",
                          justifyContent: "flex-end",
                        }}
                      >
                        <span style={smallMutedStyle}>Auto</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleEntry(a);
                          }}
                          disabled={busy}
                          style={{
                            ...switchStyle,
                            background: autoEntry ? "var(--primary-soft)" : "var(--surface-alt)",
                          }}
                          title="Cambiar auto-entry"
                        >
                          <span
                            style={{
                              position: "absolute",
                              top: "3px",
                              left: autoEntry ? "25px" : "3px",
                              width: "22px",
                              height: "22px",
                              borderRadius: "999px",
                              background: autoEntry ? "var(--primary)" : "var(--text-secondary)",
                              transition: "all var(--transition-base)",
                            }}
                          />
                        </button>
                      </label>

                      <IconBtn
                        title="Eliminar cuenta"
                        onClick={(e) => {
                          e?.preventDefault();
                          e?.stopPropagation();
                          deleteAccount(a);
                        }}
                      >
                        🗑
                      </IconBtn>
                    </div>
                  );
                })}
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: "10px",
              }}
              className="accounts-create-grid"
            >
              <select
                value={addingAcc.provider}
                onChange={(e) =>
                  setAddingAcc((x) => ({
                    ...x,
                    provider: e.target.value as ProviderValue,
                  }))
                }
                style={selectStyle}
              >
                {PROVIDERS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>

              <Input
                value={addingAcc.accountIdentifier}
                onChange={(e) =>
                  setAddingAcc((x) => ({ ...x, accountIdentifier: e.target.value }))
                }
                placeholder="Identificador"
              />

              <Input
                value={addingAcc.displayName}
                onChange={(e) =>
                  setAddingAcc((x) => ({ ...x, displayName: e.target.value }))
                }
                placeholder="Nombre visible"
              />
            </div>

            <div style={{ ...smallMutedStyle, marginTop: "12px" }}>
              Para Mercado Libre, la cuenta se crea primero en estado pendiente y se vincula
              luego por OAuth. El cambio de auto-entry usa solo el endpoint específico.
            </div>

            <div style={{ marginTop: "14px" }}>
              <Button onClick={createAndLinkAccount} disabled={busy}>
                {busy ? "Procesando..." : "Agregar cuenta"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {tab === "DIRECCIONES" && (
        <Card>
          <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
            MVP: Direcciones de la tienda pendientes de tabla y endpoints.
          </div>
        </Card>
      )}

      {tab === "CONTACTOS" && (
        <Card>
          <div style={{ marginBottom: "16px" }}>
            <h4 style={{ ...sectionTitleStyle, marginBottom: "6px" }}>Contactos del store</h4>
            <p style={sectionTextStyle}>
              Vista preparada para reflejar automáticamente los datos del o los sellers
              asignados a esta tienda.
            </p>
          </div>

          {linkedSellers.length === 0 ? (
            <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
              Sin sellers vinculados todavía. Cuando asignes uno o más sellers, acá vas a ver
              sus datos de contacto.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "16px",
              }}
              className="store-contacts-grid"
            >
              {linkedSellers.map((seller) => {
                const fullName =
                  seller.fullName ||
                  buildFullName(seller.firstName, seller.lastName) ||
                  seller.username ||
                  `Seller ${seller.id}`;

                return (
                  <div key={seller.id} style={contactCardStyle}>
                    <div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 700,
                          color: "var(--text-primary)",
                        }}
                      >
                        {fullName}
                      </div>

                      {seller.razon_social ? (
                        <div style={{ ...smallMutedStyle, marginTop: "4px" }}>
                          {seller.razon_social}
                        </div>
                      ) : null}
                    </div>

                    <div style={{ display: "grid", gap: "10px" }}>
                      <div style={providerCardStyle}>
                        <div style={smallMutedStyle}>Username</div>
                        <div style={{ marginTop: "6px" }}>{seller.username || "—"}</div>
                      </div>

                      <div style={providerCardStyle}>
                        <div style={smallMutedStyle}>Email</div>
                        <div style={{ marginTop: "6px" }}>{seller.email || "—"}</div>
                      </div>

                      <div style={providerCardStyle}>
                        <div style={smallMutedStyle}>Teléfono</div>
                        <div style={{ marginTop: "6px" }}>{seller.phone || "—"}</div>
                      </div>

                      <div style={providerCardStyle}>
                        <div style={smallMutedStyle}>Seller ID</div>
                        <div style={{ marginTop: "6px", fontFamily: "monospace" }}>
                          {seller.id}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {err && (
        <div style={{ marginTop: "16px", color: "var(--danger)", fontSize: "14px" }}>
          {err}
        </div>
      )}

      <div style={{ marginTop: "24px", textAlign: "right" }}>
        <Button variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      {selectedAccount && (
        <AccountDetailModal
          storeId={store.id ?? 0}
          account={selectedAccount}
          token={token}
          onClose={() => setSelectedAccount(null)}
          onUpdated={async () => {
            await loadAccounts();
            await onChanged?.();
          }}
        />
      )}

      <style>
        {`
          @media (max-width: 980px) {
            .edit-store-head-grid {
              grid-template-columns: 1fr !important;
            }

            .edit-store-tabs-grid {
              grid-template-columns: 1fr !important;
            }

            .accounts-create-grid {
              grid-template-columns: 1fr !important;
            }

            .store-contacts-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </Modal>
  );
}

/* ================== Modal detalle de account ================== */
function AccountDetailModal({
  storeId,
  account,
  token,
  onClose,
  onUpdated,
}: AccountDetailModalProps) {
  const resolvedProvider = getResolvedProvider(account);
  const linked = isMeliLinked(account);
  const tokenMlUserId = account.__tokenMlUserId ?? null;
  const currentMlUserId = getAccMlUserId(account);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState(
    account.display_name || account.displayName || ""
  );
  const [provider, setProvider] = useState(
    isTokenMeliCandidate({
      access_token: account.__hasOAuthToken ? "x" : null,
      refresh_token: account.__hasOAuthToken ? "x" : null,
      user_id: tokenMlUserId,
    })
      ? "Mercadolibre"
      : resolvedProvider || ""
  );
  const [mlUserId, setMlUserId] = useState<string>(
    currentMlUserId != null ? String(currentMlUserId) : tokenMlUserId != null ? String(tokenMlUserId) : ""
  );
  const [autoEntry, setAutoEntry] = useState(getAutoEntry(account));

  const providerLocked = tokenMlUserId != null || account.__hasOAuthToken === true;

  useEffect(() => {
    setDisplayName(account.display_name || account.displayName || "");
    setProvider(
      tokenMlUserId != null || account.__hasOAuthToken === true
        ? "Mercadolibre"
        : resolvedProvider || ""
    );
    setMlUserId(
      currentMlUserId != null
        ? String(currentMlUserId)
        : tokenMlUserId != null
        ? String(tokenMlUserId)
        : ""
    );
    setAutoEntry(getAutoEntry(account));
  }, [account, resolvedProvider, tokenMlUserId, currentMlUserId]);

  const copyLink = async () => {
    setBusy(true);
    setErr(null);
    setOkMsg(null);

    try {
      if (!MLI_APP_ID || !MLI_REDIRECT_URI) {
        throw new Error("Faltan REACT_APP_MLI_APP_ID o REACT_APP_MLI_REDIRECT_URI");
      }

      const stateObj = { accountId: account.id, storeId };
      const url = buildMeliAuthUrl(stateObj);

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        window.prompt("Copiá el link manualmente:", url);
      }

      setOkMsg("Link copiado.");
    } catch (e) {
      console.error("ML copyLink error:", e);
      setErr(e instanceof Error ? e.message : "Error copiando el link");
    } finally {
      setBusy(false);
    }
  };

  const handleToggleEntry = async () => {
    setBusy(true);
    setErr(null);
    setOkMsg(null);

    try {
      const next = !autoEntry;
      await updateAccountEntryOnly(token, Number(account.id), next);
      setAutoEntry(next);
      setOkMsg(`Auto-entry ${next ? "activado" : "desactivado"}.`);
      await onUpdated?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error actualizando auto-entry");
    } finally {
      setBusy(false);
    }
  };

  const handleRecoverAccount = async () => {
    setBusy(true);
    setErr(null);
    setOkMsg(null);

    try {
      const normalizedProvider =
        providerLocked || tokenMlUserId != null ? "Mercadolibre" : provider.trim();

      const normalizedMlUserId =
        mlUserId.trim() !== ""
          ? Number(mlUserId)
          : tokenMlUserId != null
          ? Number(tokenMlUserId)
          : null;

      if (!normalizedProvider) {
        throw new Error("Completá el provider.");
      }

      if (normProvider(normalizedProvider) === "mercadolibre" && !normalizedMlUserId) {
        throw new Error("Completá el ml_user_id para la cuenta de Mercado Libre.");
      }

      await updateAccountSafely(token, {
        id: Number(account.id),
        provider: normalizedProvider,
        display_name: displayName.trim() || "Pendiente",
        ml_user_id: normalizedMlUserId,
        auto_entry: autoEntry,
      });

      setOkMsg("Cuenta actualizada correctamente.");
      await onUpdated?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error actualizando la cuenta");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <AccountLogo provider={provider || resolvedProvider} />
          <span>Cuenta</span>
          <span style={{ color: "var(--text-secondary)" }}>
            — {displayName || getAccDisplayName(account)}
          </span>
          <span style={{ marginLeft: "8px", color: linked ? "var(--success)" : "var(--danger)" }}>
            {linked ? "✓ Vinculado" : "✗ No vinculado"}
          </span>
        </div>
      }
      onClose={onClose}
      width="980px"
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
          marginBottom: "16px",
        }}
        className="account-detail-grid"
      >
        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Provider</div>
          {providerLocked ? (
            <div style={{ marginTop: "6px", textTransform: "capitalize", fontWeight: 700 }}>
              Mercado Libre
            </div>
          ) : (
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              style={{ ...selectStyle, marginTop: "8px" }}
            >
              <option value="">Seleccionar</option>
              <option value="Mercadolibre">Mercado Libre</option>
              <option value="Tiendanube">Tienda Nube</option>
              <option value="Shopify">Shopify</option>
              <option value="WooCommerce">WooCommerce</option>
              <option value="Otros">Otros</option>
            </select>
          )}
          {providerLocked ? (
            <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
              Provider bloqueado porque hay token ML asociado.
            </div>
          ) : null}
        </div>

        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Account ID (interno)</div>
          <div style={{ marginTop: "6px", fontFamily: "monospace" }}>{account.id}</div>
        </div>

        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Nombre visible</div>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Nombre visible"
          />
        </div>

        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>ML user_id</div>
          <Input
            value={mlUserId}
            onChange={(e) => setMlUserId(e.target.value)}
            placeholder="ML user_id"
            disabled={tokenMlUserId != null}
          />
          {tokenMlUserId != null ? (
            <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
              Detectado desde tokens: {tokenMlUserId}
            </div>
          ) : null}
        </div>

        <div style={{ ...providerCardStyle, gridColumn: "1 / -1" }}>
          <div style={smallMutedStyle}>Identificador</div>
          <div style={{ marginTop: "6px", fontFamily: "monospace" }}>
            {isMeli(provider || resolvedProvider)
              ? mlUserId || "— (pendiente de completar)"
              : getAccIdentifier(account)}
          </div>
          <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
            ML: se completa al finalizar OAuth o se recupera desde tokens · TN: shop id/url ·
            Shopify: domain · WooCommerce: url
          </div>
        </div>

        {isMeli(provider || resolvedProvider) && (
          <div style={{ ...providerCardStyle, gridColumn: "1 / -1" }}>
            <div style={smallMutedStyle}>Estado OAuth</div>
            <div style={{ marginTop: "6px", fontFamily: "monospace" }}>
              {linked
                ? "Vinculada con token activo"
                : account.__hasOAuthToken
                ? "Hay token ML, falta reconstruir metadata"
                : "Pendiente de vinculación"}
            </div>
          </div>
        )}

        <div style={{ ...providerCardStyle, gridColumn: "1 / -1" }}>
          <div style={smallMutedStyle}>Auto-entry</div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                {autoEntry ? "Activo" : "Inactivo"}
              </div>
              <div style={{ ...smallMutedStyle, marginTop: "4px" }}>
                Este cambio usa exclusivamente el endpoint específico de entry.
              </div>
            </div>

            <button
              type="button"
              onClick={handleToggleEntry}
              disabled={busy}
              style={{
                ...switchStyle,
                background: autoEntry ? "var(--primary-soft)" : "var(--surface-alt)",
              }}
              title="Cambiar auto-entry"
            >
              <span
                style={{
                  position: "absolute",
                  top: "3px",
                  left: autoEntry ? "25px" : "3px",
                  width: "22px",
                  height: "22px",
                  borderRadius: "999px",
                  background: autoEntry ? "var(--primary)" : "var(--text-secondary)",
                  transition: "all var(--transition-base)",
                }}
              />
            </button>
          </div>
        </div>
      </div>

      {isMeli(provider || resolvedProvider) && (
        <Card>
          <div style={{ color: "var(--text-secondary)", fontSize: "14px", marginBottom: "14px" }}>
            Vinculación Mercado Libre vía OAuth. Si la metadata de la account se rompió pero el
            token sigue existiendo, podés reconstruir provider, display_name y ml_user_id desde acá.
          </div>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Button variant="secondary" onClick={copyLink} disabled={busy}>
              Link de vinculación
            </Button>

            <Button onClick={handleRecoverAccount} disabled={busy}>
              {busy ? "Guardando..." : "Guardar cuenta"}
            </Button>
          </div>
        </Card>
      )}

      {!isMeli(provider || resolvedProvider) && (
        <div style={{ textAlign: "right" }}>
          <Button onClick={handleRecoverAccount} disabled={busy}>
            {busy ? "Guardando..." : "Guardar cuenta"}
          </Button>
        </div>
      )}

      {err && (
        <div style={{ marginTop: "14px", color: "var(--danger)", fontSize: "14px" }}>
          {err}
        </div>
      )}
      {okMsg && (
        <div style={{ marginTop: "14px", color: "var(--success)", fontSize: "14px" }}>
          {okMsg}
        </div>
      )}

      <div style={{ marginTop: "20px", textAlign: "right" }}>
        <Button variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      <style>
        {`
          @media (max-width: 720px) {
            .account-detail-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </Modal>
  );
}